import { ApplicationConfig, APP_INITIALIZER, LOCALE_ID, importProvidersFrom, Injector } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HttpInterceptorFn, provideHttpClient, withInterceptors } from '@angular/common/http';
import { authErrorInterceptor } from './core/interceptors/auth-error.interceptor';
import { otpAuthInterceptor } from './core/interceptors/otp-auth.interceptor';
import { msalGateInterceptor } from './core/interceptors/msal-gate.interceptor';
import { serverUnavailableInterceptor } from './core/interceptors/server-unavailable.interceptor';
import { projectArchivedInterceptor } from './core/interceptors/project-archived.interceptor';
import { provideRouter, RouterConfigOptions, withRouterConfig } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { CommonModule } from '@angular/common';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MsalGuard, MsalInterceptor, MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { IPublicClientApplication } from '@azure/msal-browser';
import { MSAL_INSTANCE, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';
import { routes } from './app.routes';
import { ConfigService } from './core/config/config.service';
import { createMsalInstance, createMsalInterceptorConfig, createMsalGuardConfig } from './core/auth/auth.config';
import { MsalSessionService } from './core/auth/msal-session.service';
import { provideDeDateAdapter } from './shared/date/de-date-adapter';

registerLocaleData(localeDe, 'de');
registerLocaleData(localeDe, 'de-DE');

// serverUnavailableInterceptor sitzt innerhalb von authErrorInterceptor, damit ein 401/403 aus einer
// Wiederholung dort behandelt wird, und vor den Token-Interceptoren, damit jede Wiederholung ein
// aktuelles Token bekommt.
export const APP_HTTP_INTERCEPTORS: HttpInterceptorFn[] = [
  projectArchivedInterceptor,
  authErrorInterceptor,
  serverUnavailableInterceptor,
  otpAuthInterceptor,
  msalGateInterceptor,
];

export const APP_ROUTER_CONFIG: RouterConfigOptions = { canceledNavigationResolution: 'computed' };

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'de-DE' },
    ...provideDeDateAdapter(),
    provideAnimations(),
    provideHttpClient(withInterceptors(APP_HTTP_INTERCEPTORS)),
    provideRouter(routes, withRouterConfig(APP_ROUTER_CONFIG)),
    importProvidersFrom(
      CommonModule,
      TranslateModule.forRoot({
        fallbackLang: 'de',
        loader: provideTranslateHttpLoader({
          prefix: './assets/i18n/',
          suffix: '.json',
        }),
      }),
    ),
    ConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: (configService: ConfigService, injector: Injector) => async () => {
        await configService.load();
        const msalService = injector.get(MsalService);
        await firstValueFrom(msalService.initialize());
        const result = await firstValueFrom(msalService.handleRedirectObservable(), { defaultValue: null });
        const msalSession = injector.get(MsalSessionService);
        if (result?.account) {
          msalSession.startAzureSession(result.account);
        } else {
          msalSession.restoreActiveAccount();
        }
      },
      deps: [ConfigService, Injector],
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: (configService: ConfigService): IPublicClientApplication => {
        return createMsalInstance(configService.azure.tenantId, configService.azure.clientId);
      },
      deps: [ConfigService],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: () => createMsalGuardConfig(),
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: (configService: ConfigService) => createMsalInterceptorConfig(configService.azure.clientId),
      deps: [ConfigService],
    },
    MsalGuard,
    MsalService,
    MsalBroadcastService,
    // Not registered via HTTP_INTERCEPTORS: msalGateInterceptor invokes it directly and only
    // when no OTP session is active (see msal-gate.interceptor.ts).
    MsalInterceptor,
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        verticalPosition: 'top',
        horizontalPosition: 'center',
        duration: 3000,
        politeness: 'polite',
      },
    },
  ],
};
